// Forecast Page

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { FailureFilters, DEFAULT_FAILURE_FILTERS } from '@/types/filters';
import { Workspace } from '@/types/workspace';
import { WeibullAnalysisResult } from '@/utils/weibull';
import { ForecastProject, ForecastConfig, SimulationMetadata } from '@/types/forecast';
import { useForecastCalculation } from '@/hooks/useForecastCalculation';
import { useForecastProject, useForecastProjectDetail } from '@/hooks/useForecastProject';
import { useLayout } from '@/contexts';
import { WaveLoading, PageToolbar, ToolbarIcons, ResponsiveGrid } from '@/components/ui';
import WorkspaceErrorBoundary from '@/components/error/WorkspaceErrorBoundary';
import {
  ForecastChart,
  ForecastStats,
  ForecastTable,
  ForecastSidebarController,
  LoadAnalysisDialog,
  SaveForecastDialog,
  LoadForecastDialog
} from '@/components/forecast';
import { ErrorModal } from '@/components/error/ErrorModal';
import { AxiosError } from 'axios';

/**
 * Main forecasting page component
 */
const ForecastPage: React.FC = () => {
  const { workspaceId: _workspaceId } = useParams<{ workspaceId: string }>();
  const location = useLocation();

  // State management
  const [sourceAnalysis, setSourceAnalysis] = useState<WeibullAnalysisResult | null>(null);
  const [sourceWorkspace, setSourceWorkspace] = useState<Workspace | null>(null);
  const [filters, setFilters] = useState<FailureFilters>(DEFAULT_FAILURE_FILTERS);
  const [forecastName, setForecastName] = useState('');
  const [existingForecast, setExistingForecast] = useState(false)
  const [existingForecastData, setExistingForecastData] = useState<ForecastProject | null>(null);
  const [apiError, setApiError] = useState(null);
  const [forecastLoaded, setForecastLoaded] = useState(false);

  // Forecast configuration state
  const [forecastConfiguration, setForecastConfiguration] = useState<ForecastConfig>(() => {
    // Default to 1st of current month
    const today = new Date();
    const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    return {
      horizonYears: 5,
      granularityMonths: 12, // Default to yearly
      startDate: firstOfMonth.toISOString(), // Always 1st of month
      useMonteCarloSimulation: true,
      confidenceLevel: 0.90, // Default to 90% confidence intervals
      includeConfidenceIntervals: true,
      includePlannedFailures: true, // Default to include planned failures
      parameterSamples: 100, // Default to Medium quality (100 samples)
      trajectoriesPerParameter: 50 // Default to Medium quality (50 trajectories)
    };
  });

  // Simulation metadata state
  const [simulationMetadata, setSimulationMetadata] = useState<SimulationMetadata | null>(null);

  // Modal states
  const [showLoadAnalysisDialog, setShowLoadAnalysisDialog] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showLoadForecastDialog, setShowLoadForecastDialog] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false)

  // Forecast calculation
  const {
    forecastResults,
    isCalculating,
    turbinesLoading: _turbinesLoading,
    calculateForecast,
    clearResults,
    setForecastResults,
    populationSize,
    forecastSubsetSize,
    canCalculate,
    validationErrors,
    simulationMetadata: hookSimulationMetadata,
    calculationProgress,
    progressText
  } = useForecastCalculation({
    sourceAnalysis,
    filters,
    configuration: forecastConfiguration
  });

  // Workspace management hook
  const { saveForecastProject, updateForecastProject, isLoading: isSavingWorkspace } = useForecastProject();

  // Extract forecast ID from location state or sessionStorage
  const forecastIdToLoad = location.state?.forecastId ||
    (() => {
      const sessionData = sessionStorage.getItem('loadForecastId');
      if (sessionData) {
        sessionStorage.removeItem('loadForecastId'); // Clean up after use
        return sessionData;
      }
      return null;
    })();

  // Load specific forecast if ID is provided
  const { data: forecastToLoad, isLoading: isLoadingForecast } = useForecastProjectDetail(forecastIdToLoad);
  if (forecastLoaded && forecastIdToLoad !== existingForecastData?.id) {
    setForecastLoaded(false);
  }

  // Layout context for responsive sidebar
  const { getContentAreaStyle } = useLayout();

  // Sync simulation metadata from hook
  useEffect(() => {
    setSimulationMetadata(hookSimulationMetadata);
  }, [hookSimulationMetadata]);


  // Auto-generate forecast name based on source
  useEffect(() => {
    if (sourceWorkspace && !forecastName) {
      const timestamp = new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
      setForecastName(`${sourceWorkspace.name} - Forecast (${timestamp})`);
    }
  }, [sourceWorkspace, forecastName]);

  // Handle calculate forecast
  const handleCalculateForecast = async () => {
    await calculateForecast();
  };


  // Handle loading source analysis
  const handleLoadAnalysis = (workspace: Workspace) => {
    // Load new workspace, clear existing forecast
    setExistingForecast(false);
    setExistingForecastData(null);

    setSourceWorkspace(workspace);
    setSourceAnalysis(workspace.results!);

    // Auto-apply the same filters used in the source analysis
    const sourceFilters = workspace.configuration.filters;

    // Set forecast filters to match source analysis filters
    const inheritedFilters: FailureFilters = {
      ...DEFAULT_FAILURE_FILTERS,
      // Copy filters from source analysis
      sites: sourceFilters.sites || [],
      componentsMajor: sourceFilters.componentsMajor || [],
      turbineModels: sourceFilters.turbineModels || [],
      failureModes: sourceFilters.failureModes || [],
      // Copy date range if available
      dateFrom: sourceFilters.dateFrom,
      dateTo: sourceFilters.dateTo,
      // Copy other relevant filters
      operationTimeMin: sourceFilters.operationTimeMin,
      operationTimeMax: sourceFilters.operationTimeMax
    };

    setFilters(inheritedFilters);
    setShowLoadAnalysisDialog(false);
    clearResults();
  };

  // Handle loading saved forecast
  const handleLoadForecast = useCallback((forecast: ForecastProject) => {
    // Close the modal first to prevent flickering
    setShowLoadForecastDialog(false);

    // Clear existing results first to reset state
    clearResults();

    // Load the source analysis if available
    if (forecast.sourceAnalysisWorkspaceId) {
      const sourceWorkspace = {
        id: forecast.sourceAnalysisWorkspaceId,
        name: forecast.sourceAnalysisName || 'Unknown Analysis',
        results: {
          parameters: {
            shape: forecast.weibullParameters.beta,
            scale: forecast.weibullParameters.eta,
            rSquared: forecast.weibullParameters.rSquared
          }
        }
      } as Workspace;

      const sourceAnalysis = {
        parameters: {
          shape: forecast.weibullParameters.beta,
          scale: forecast.weibullParameters.eta,
          rSquared: forecast.weibullParameters.rSquared
        }
      } as WeibullAnalysisResult;

      setSourceWorkspace(sourceWorkspace);
      setSourceAnalysis(sourceAnalysis);
    }

    // Restore configuration
    setFilters(forecast.configuration.filters);
    setForecastName(forecast.name);

    // Restore forecast configuration if available
    if (forecast.configuration.forecastParams) {
      setForecastConfiguration({
        horizonYears: forecast.configuration.forecastParams.horizonYears,
        granularityMonths: forecast.configuration.forecastParams.granularityMonths || 12,
        startDate: forecast.configuration.forecastParams.startDate || new Date().toISOString(),
        useMonteCarloSimulation: forecast.configuration.forecastParams.useMonteCarloSimulation ?? true,
        confidenceLevel: forecast.configuration.forecastParams.confidenceLevel || 0.90,
        includeConfidenceIntervals: forecast.configuration.forecastParams.includeConfidenceIntervals ?? true,
        includePlannedFailures: forecast.configuration.forecastParams.includePlannedFailures ?? true,
        parameterSamples: forecast.configuration.forecastParams.parameterSamples || 100,
        trajectoriesPerParameter: forecast.configuration.forecastParams.trajectoriesPerParameter || 50
      });
    }

    // Restore forecast results if available (do this after a small delay to allow state to settle)
    if (forecast.results && forecast.results.length > 0) {
      setTimeout(() => {
        setForecastResults(forecast.results, undefined);
      }, 100);
    }
    setExistingForecast(true);
    setExistingForecastData(forecast);
    setForecastLoaded(true);
  }, [clearResults, setForecastResults]); // Include necessary dependencies

  // Auto-load forecast from URL or sessionStorage
  useEffect(() => {
    if (forecastLoaded) {
      return;
    }
    if (forecastToLoad && !isLoadingForecast) {
      handleLoadForecast(forecastToLoad);
    }
  }, [forecastToLoad, isLoadingForecast, handleLoadForecast, forecastLoaded]);

  // Handle saving forecast workspace
  const handleSaveForecast = async () => {
    if (!sourceAnalysis || !forecastResults.length) return;

    let forecastWorkspace: ForecastProject
    if (existingForecast) {
      forecastWorkspace = existingForecastData;
      forecastWorkspace.metadata.updatedAt = new Date().toISOString();
      forecastWorkspace.metadata.version++;
    } else {
      forecastWorkspace = {
        id: crypto.randomUUID(),
        name: forecastName,
        sourceAnalysisWorkspaceId: sourceWorkspace?.id || '',
        sourceAnalysisName: sourceWorkspace?.name || 'Unknown Analysis',
        configuration: null,
        weibullParameters: null,
        results: null,
        metadata: {
          userId: 'current-user', // TODO: Get from auth context
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          version: 1,
          tags: [sourceWorkspace?.name || 'Unknown Analysis']
        }
      };
    }

    forecastWorkspace.configuration = {
      filters,
      forecastParams: {
        horizonYears: forecastConfiguration.horizonYears,
        granularityMonths: forecastConfiguration.granularityMonths,
        startDate: forecastConfiguration.startDate,
        useMonteCarloSimulation: forecastConfiguration.useMonteCarloSimulation,
        confidenceLevel: forecastConfiguration.confidenceLevel,
        includeConfidenceIntervals: forecastConfiguration.includeConfidenceIntervals,
        includePlannedFailures: forecastConfiguration.includePlannedFailures,
        parameterSamples: forecastConfiguration.parameterSamples,
        trajectoriesPerParameter: forecastConfiguration.trajectoriesPerParameter
      }
    };

    forecastWorkspace.weibullParameters = {
      beta: sourceAnalysis.parameters.shape,
      eta: sourceAnalysis.parameters.scale,
      rSquared: sourceAnalysis.parameters.rSquared,
      confidenceLevel: 0.95
    };

    forecastWorkspace.results = forecastResults;

    try {
      existingForecast ? await updateForecastProject.mutateAsync({ id: forecastWorkspace.id, updates: forecastWorkspace }) : await saveForecastProject.mutateAsync(forecastWorkspace);
      setShowSaveDialog(false);
      setExistingForecast(true);
      setExistingForecastData(forecastWorkspace);
      // Success notification would be handled by the mutation
    } catch (error) {
      // Error handling is managed by the mutation
      handleAPIError(error)
    }
  };

  // Handle API error
  const handleAPIError = async (error: AxiosError) => {
    let errorMessage = error.response?.data['error'];
    if (!errorMessage) {
      errorMessage = "An error occurred with the last request";
    }
    setApiError(errorMessage);
    setShowErrorModal(true);
  }

  return (
    <WorkspaceErrorBoundary>
      <div className="min-h-screen bg-gray-50">

        {/* Tool Page Header */}
        <PageToolbar
          title="Forecasting"
          icon={ToolbarIcons.NewForecast}
          buttons={[
            {
              key: 'load-workspace',
              label: 'Load Workspace',
              icon: ToolbarIcons.LoadWorkspace,
              onClick: () => setShowLoadAnalysisDialog(true),
              variant: 'secondary',
              tooltip: 'Load source analysis for forecast'
            },
            {
              key: 'calculate-forecast',
              label: isCalculating ? 'Calculating...' : 'Calculate Forecast',
              icon: ToolbarIcons.Calculate,
              onClick: handleCalculateForecast,
              variant: 'primary',
              disabled: !canCalculate || isCalculating,
              loading: isCalculating,
              loadingText: 'Calculating...',
              tooltip: 'Calculate forecast based on current settings'
            },
            {
              key: 'save-forecast',
              label: 'Save Forecast',
              icon: ToolbarIcons.SaveForecast,
              onClick: () => setShowSaveDialog(true),
              variant: 'primary',
              disabled: !forecastResults.length,
              tooltip: 'Save current forecast'
            },
            {
              key: 'load-forecast',
              label: 'Load Forecast',
              icon: ToolbarIcons.LoadForecast,
              onClick: () => setShowLoadForecastDialog(true),
              variant: 'secondary',
              tooltip: 'Load saved forecast'
            }
          ]}
          statusInfo={{
            sourceWorkspace: sourceWorkspace?.name,
            validationMessage: validationErrors.length > 0 ? validationErrors[0] : undefined,
            isWarning: validationErrors.length > 0,
            workspaceName: forecastName || 'Untitled Forecast'
          }}
        />

        {/* Main content layout */}
        <div className="flex flex-1 overflow-hidden">
          {/* Collapsible Sidebars */}
          <ForecastSidebarController
            // Filter props
            filters={filters}
            onFiltersChange={setFilters}
            sourceWorkspace={sourceWorkspace}
            // Configuration props
            configuration={forecastConfiguration}
            onConfigurationChange={setForecastConfiguration}
            populationSize={populationSize}
            forecastSubsetSize={forecastSubsetSize}
            // Parameters props
            sourceAnalysis={sourceAnalysis}
            simulationMetadata={simulationMetadata}
            // Common props
            disabled={!sourceAnalysis}
          />

          {/* Main content area */}
          <div className="flex-1 overflow-y-auto min-w-0">
            <div style={getContentAreaStyle()}>

              {/* Stats Cards - Using ResponsiveGrid */}
              <ResponsiveGrid minCardWidth={250} gap="md" className="mb-6">
                <ForecastStats
                  results={forecastResults}
                  isLoading={isCalculating}
                  confidenceLevel={forecastConfiguration.confidenceLevel}
                  populationSize={populationSize}
                  forecastSubsetSize={forecastSubsetSize}
                />
              </ResponsiveGrid>

              {/* Chart */}
              <div className="mb-6">
                <ForecastChart
                  results={forecastResults}
                  isLoading={isCalculating}
                  title="Failure Forecast"
                  includeConfidenceIntervals={forecastConfiguration.includeConfidenceIntervals}
                  includePlannedFailures={forecastConfiguration.includePlannedFailures}
                  // Pass the granularity from results if available, otherwise from configuration
                  // This prevents axis labels from changing before recalculation
                  granularityMonths={forecastResults.length > 0 ?
                    (forecastResults[0]?.granularityMonths || forecastConfiguration.granularityMonths) :
                    forecastConfiguration.granularityMonths}
                />
              </div>

              {/* Table */}
              <div className="min-h-[20rem] border-t border-gray-200 pt-6">
                <ForecastTable
                  results={forecastResults}
                  isLoading={isCalculating}
                  // Use granularity from results if available to match chart display
                  granularityMonths={forecastResults.length > 0 ?
                    (forecastResults[0]?.granularityMonths || forecastConfiguration.granularityMonths) :
                    forecastConfiguration.granularityMonths}
                  includePlannedFailures={forecastConfiguration.includePlannedFailures}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Modals */}
        {showLoadAnalysisDialog && (
          <LoadAnalysisDialog
            isOpen={showLoadAnalysisDialog}
            onClose={() => setShowLoadAnalysisDialog(false)}
            onLoad={handleLoadAnalysis}
          />
        )}

        {showSaveDialog && (
          <SaveForecastDialog
            isOpen={showSaveDialog}
            forecastName={forecastName}
            onNameChange={setForecastName}
            onClose={() => setShowSaveDialog(false)}
            onSave={handleSaveForecast}
            isLoading={isSavingWorkspace}
          />
        )}

        {showLoadForecastDialog && (
          <LoadForecastDialog
            open={showLoadForecastDialog}
            onClose={() => setShowLoadForecastDialog(false)}
            onSelect={handleLoadForecast}
          />
        )}

        {showErrorModal && (
          <ErrorModal open={showErrorModal} onClose={() => setShowErrorModal(false)} error={apiError} />
        )}

        {/* Full-screen loading overlay */}
        <WaveLoading
          fullScreen={true}
          size="lg"
          variant="wave"
          color="bright-blue"
          text="Calculating forecast..."
          progress={calculationProgress}
          progressText={progressText || 'Processing...'}
          style={{
            opacity: isCalculating ? 1 : 0,
            pointerEvents: isCalculating ? 'auto' : 'none',
            transition: 'opacity 0.2s ease-in-out'
          }}
        />
      </div>
    </WorkspaceErrorBoundary>
  );
};

export default ForecastPage;
